import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaVideo, FaCalendarAlt, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import useBancaTecnicoStore from '../../stores/bancaTecnicoStore';
import { BancaResponseTecnicoPage, BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import './bancasTecnicoPage.css';

const formatarDataParaBrasileiro = (data: string | null): string | null => {
    if (!data) return null;
    const partes = data.split('-');
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const BancasTecnicoPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const { bancaTecnico, setBancaTecnico } = useBancaTecnicoStore();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs(createBreadcrumbs.withoutProcessos([
            { path: `/tecnico/bancas`, breadcrumb: 'Upload de Vídeos' },
        ]));
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const objetos: BancaResponseTecnicoPage[] = await BancaViewModel.getBancasTecnicoPage();
                setBancaTecnico(objetos);
            } catch (error) {
                console.error('Erro ao buscar bancas:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [setBancaTecnico]);

    const handleVisualizarCandidatosBancaParaUpload = (processoId: string, bancaId: string) => {
        navigate(`/processos/${processoId}/bancas/${bancaId}/candidatos/videos/upload`);
    };

    const bancasPorProcesso = bancaTecnico.reduce((acc, banca) => {
        const chaveProcesso = banca.processoNome || 'sem-identificador';
        
        if (!acc[chaveProcesso]) {
            acc[chaveProcesso] = {
                processoNome: banca.processoNome || 'Processo sem nome',
                bancas: []
            };
        }
        
        if (!acc[chaveProcesso].bancas.some(existente => existente.bancaId === banca.bancaId)) {
            acc[chaveProcesso].bancas.push(banca);
        }
        
        return acc;
    }, {} as Record<string, { processoNome: string; bancas: BancaResponseTecnicoPage[] }>);

    const totalPendentes = bancaTecnico.reduce((total, banca) => total + banca.numeroCandidatosComVotacaoFechada, 0);

    if (loading) {
        return (
            <div className="bancas-tecnico-container">
                <div className="loading-container">
                    <Spinner animation="border" variant="primary" />
                    <p>Carregando bancas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bancas-tecnico-container">
            <div className="header-section">
                <h2><FaVideo className="me-2" />Upload de Vídeos</h2>
                <p className="text-muted">Gerencie o upload de vídeos dos candidatos para as bancas de heteroidentificação</p>
                
                {totalPendentes > 0 && (
                    <div className="alert alert-warning d-flex align-items-center">
                        <FaExclamationTriangle className="me-2" />
                        <span>Existem <strong>{totalPendentes}</strong> uploads pendentes</span>
                    </div>
                )}
            </div>

            {Object.entries(bancasPorProcesso).length === 0 ? (
                <Card className="empty-state">
                    <Card.Body className="text-center py-5">
                        <FaVideo size={48} className="text-muted mb-3" />
                        <h5>Nenhuma banca encontrada</h5>
                        <p className="text-muted">Não há bancas disponíveis para upload de vídeos no momento.</p>
                    </Card.Body>
                </Card>
            ) : (
                Object.entries(bancasPorProcesso).map(([identificadorProcesso, { processoNome, bancas }]) => (
                    <Card key={identificadorProcesso} className="processo-card mb-4">
                        <Card.Header>
                            <h5 className="mb-0">{processoNome}</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="bancas-grid">
                                {bancas.map((banca) => {
                                    const temUploadsPendentes = banca.numeroCandidatosComVotacaoFechada > 0;
                                    return (
                                        <div key={`${banca.bancaId}-${banca.processoId}`} className={`banca-item ${temUploadsPendentes ? 'pendente' : 'completo'}`}>
                                            <div className="banca-header">
                                                <div className="banca-title">
                                                    <h6>{banca.tipoBanca?.nome || 'Tipo não disponível'}</h6>
                                                    {temUploadsPendentes ? (
                                                        <Badge bg="warning" text="dark">
                                                            <FaExclamationTriangle className="me-1" />Pendente
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="success">
                                                            <FaCheckCircle className="me-1" />Completo
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="banca-info">
                                                <div className="info-item">
                                                    <FaCalendarAlt className="icon" />
                                                    <span>{formatarDataParaBrasileiro(banca.dataDeBancaResponse.data)}</span>
                                                </div>
                                                <div className="info-item">
                                                    <FaClock className="icon" />
                                                    <span>{banca.dataDeBancaResponse.horaInicial} - {banca.dataDeBancaResponse.horaFinal}</span>
                                                </div>
                                            </div>

                                            <div className="uploads-info">
                                                <span className="uploads-label">Uploads restantes:</span>
                                                <Badge 
                                                    bg={temUploadsPendentes ? 'warning' : 'success'} 
                                                    text={temUploadsPendentes ? 'dark' : 'white'}
                                                    className="uploads-count"
                                                >
                                                    {banca.numeroCandidatosComVotacaoFechada}
                                                </Badge>
                                            </div>

                                            <Button
                                                variant={temUploadsPendentes ? 'warning' : 'outline-success'}
                                                className="action-button"
                                                onClick={() => handleVisualizarCandidatosBancaParaUpload(banca.processoId, banca.bancaId)}
                                            >
                                                <FaVideo className="me-2" />
                                                {temUploadsPendentes ? 'Realizar Uploads' : 'Visualizar'}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card.Body>
                    </Card>
                ))
            )}
        </div>
    );
};

export default BancasTecnicoPage;
